import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useNotifications } from '../../contexts/NotificationContext';

const Notifications = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [categoryFilter, setCategoryFilter] = useState('all'); // all, appointment, estimate, payment, system

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#666';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'appointment': return '#2196f3';
      case 'estimate': return '#4caf50';
      case 'payment': return '#9c27b0';
      case 'system': return '#607d8b';
      default: return '#666';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'appointment': return '📅';
      case 'estimate': return '💰';
      case 'payment': return '💳';
      case 'system': return '⚙️';
      default: return '📢';
    }
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    // Filter by read status
    if (filter === 'unread' && notification.read) return false;
    if (filter === 'read' && !notification.read) return false;
    
    // Filter by category
    if (categoryFilter !== 'all' && notification.category !== categoryFilter) return false;
    
    return true;
  });

  // Group notifications by date
  const groupedNotifications = filteredNotifications.reduce((groups, notification) => {
    const date = notification.createdAt ? notification.createdAt.toDateString() : 'Unknown Date';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(notification);
    return groups;
  }, {});

  const getDateLabel = (dateString) => {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    
    if (dateString === today) return 'Today';
    if (dateString === yesterday) return 'Yesterday';
    return dateString;
  };

  return (
    <Layout>
      <div style={{ padding: '1rem 2rem' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <img 
                src="/notification.png" 
                alt="Notifications" 
                style={{ 
                  width: '48px', 
                  height: '48px', 
                  objectFit: 'contain'
                }} 
              />
              Notifications
            </h1>
              <p style={{ color: '#666', margin: '0.5rem 0 0 0' }}>
              {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
              </p>
          </div>
          
          {unreadCount > 0 && (
            <button 
              onClick={handleMarkAllRead}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}
            >
              Mark All Read
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '1.5rem'
        }}>
          <div style={{ 
            display: 'flex', 
            gap: '2rem', 
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {/* Read Status Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                color: '#333'
              }}>
                Status
              </label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
              >
                <option value="all">All Notifications</option>
                <option value="unread">Unread Only</option>
                <option value="read">Read Only</option>
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                color: '#333'
              }}>
                Category
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}
              >
                <option value="all">All Categories</option>
                <option value="appointment">📅 Appointments</option>
                <option value="estimate">💰 Estimates</option>
                <option value="payment">💳 Payments</option>
                <option value="system">⚙️ System</option>
              </select>
            </div>

            {/* Stats */}
            <div style={{ marginLeft: 'auto' }}>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>
                Showing {filteredNotifications.length} of {notifications.length} notifications
              </div>
            </div>
          </div>
        </div>
        
        {/* Notifications List */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          {Object.keys(groupedNotifications).length > 0 ? (
            Object.entries(groupedNotifications).map(([date, dayNotifications]) => (
              <div key={date}>
                {/* Date Header */}
                <div style={{
                  backgroundColor: '#f8f9fa',
                  padding: '0.75rem 1.5rem',
                  borderBottom: '1px solid #eee',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#666',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {getDateLabel(date)}
                </div>

                {/* Notifications for this date */}
                {dayNotifications.map((notification, index) => (
                <div 
                  key={notification.id} 
                    onClick={() => handleNotificationClick(notification)}
                  style={{ 
                      padding: '1.5rem',
                      borderBottom: index < dayNotifications.length - 1 ? '1px solid #f0f0f0' : 'none',
                      cursor: 'pointer',
                      backgroundColor: notification.read ? 'white' : '#f8f9ff',
                      transition: 'background-color 0.2s ease',
                      position: 'relative'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = notification.read ? '#f8f9fa' : '#f0f0ff';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = notification.read ? 'white' : '#f8f9ff';
                    }}
                  >
                    {/* Unread indicator */}
                    {!notification.read && (
                      <div style={{
                        position: 'absolute',
                        left: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#007bff',
                        borderRadius: '50%'
                      }} />
                    )}

                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '1rem',
                      marginLeft: !notification.read ? '1.5rem' : '0'
                    }}>
                      {/* Icon */}
                      <div style={{
                        fontSize: '2rem',
                        flexShrink: 0
                      }}>
                        {notification.icon || getCategoryIcon(notification.category)}
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '0.5rem',
                          flexWrap: 'wrap',
                          gap: '0.5rem'
                        }}>
                          <h3 style={{
                            margin: 0,
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#333'
                          }}>
                            {notification.title}
                          </h3>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{
                              fontSize: '0.75rem',
                              color: getCategoryColor(notification.category),
                              fontWeight: '500',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              backgroundColor: `${getCategoryColor(notification.category)}20`,
                              padding: '0.25rem 0.5rem',
                              borderRadius: '12px'
                            }}>
                              {notification.category}
                            </span>
                            
                            {notification.priority && (
                              <span style={{
                                fontSize: '0.7rem',
                                color: getPriorityColor(notification.priority),
                                fontWeight: '500',
                                textTransform: 'uppercase'
                              }}>
                                {notification.priority}
                      </span>
                    )}
                          </div>
                        </div>

                        <p style={{
                          margin: '0 0 0.75rem 0',
                          fontSize: '0.875rem',
                          color: '#666',
                          lineHeight: '1.5'
                        }}>
                          {notification.message}
                        </p>

                        <div style={{
                          fontSize: '0.8rem',
                          color: '#999'
                        }}>
                          {formatTimeAgo(notification.createdAt)}
                        </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            ))
          ) : (
            <div style={{
              padding: '4rem 2rem',
              textAlign: 'center',
              color: '#666'
            }}>
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                <img 
                  src="/notification.png" 
                  alt="No Notifications" 
                  style={{ 
                    width: '96px', 
                    height: '96px', 
                    objectFit: 'contain',
                    opacity: 0.5
                  }} 
                />
              </div>
              <h3 style={{ marginBottom: '0.5rem', color: '#333' }}>No notifications found</h3>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                {filter === 'unread' ? 'No unread notifications' : 
                 filter === 'read' ? 'No read notifications' : 
                 'No notifications match your current filters'}
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Notifications;